import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ViewPatientModal from '../components/ViewPatientModal';
import EditPatientModal from '../components/EditPatientModal';
import { BILL_TYPES } from '../constants/billingTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import { compareByDutyThenName } from '../constants/shifts';
import DutyTypePill from '../components/DutyTypePill';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface TreatingDetail {
  doctor: string;
  department: string;
  hospital: string;
}

interface Patient {
  id: number;
  patient_name: string;
  status: string;
  age: string | number;
  sex: string;
  case_start_date: string;
  duty_type?: string | null;
  case_manager?: string | null;
  address: string;
  treating_details?: TreatingDetail[];
  patient_clients: { client_name?: string; name?: string; contact_number_1?: string; contact_number?: string }[];
}

// One membership change: `included` took effect in `month` and carries forward.
interface OverrideRow {
  patient_id: number;
  month: string;      // YYYY-MM-DD, always the 1st
  included: boolean;
}

const formatDate = (d: string | undefined | null) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
};

// ── Month helpers (same "YYYY-MM" convention as Payment Collections) ─────────
function monthLabel(ym: string) {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function prevMonth(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const clientNameOf = (p: Patient) => {
  const c = [...(p.patient_clients || [])].sort((a: any, b: any) => (a.id || 0) - (b.id || 0))[0];
  return (c?.client_name || c?.name || '').trim();
};

const CurrentClientList: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth());

  // Patients ON the list for the selected month, and the month each change was
  // made in (may be earlier than the selected one — decisions carry forward).
  const [included, setIncluded] = useState<Set<number>>(new Set());
  const [changedIn, setChangedIn] = useState<Record<number, string>>({});

  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);

  const [addingCase, setAddingCase] = useState(false);
  const [addSearch, setAddSearch] = useState('');

  const [viewPatientId, setViewPatientId] = useState<number | null>(null);
  const [editPatientId, setEditPatientId] = useState<number | null>(null);
  const [pickerPatient, setPickerPatient] = useState<Patient | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const monthParam = `${selectedMonth}-01`;

  const fetchPatients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/patients/?limit=200`, { headers });
      setPatients(res.data);
    } catch (err) {
      console.error('Failed to fetch patients', err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Membership in force for the selected month. The list is opt-in: only
  // patients whose latest decision was "add" are on it, so anyone the team has
  // never touched stays off — including newly created clients.
  const fetchMembership = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/current-clients`, {
        headers, params: { month: monthParam },
      });
      const rows: OverrideRow[] = res.data?.effective || [];
      setIncluded(new Set(rows.filter(r => r.included).map(r => r.patient_id)));
      setChangedIn(Object.fromEntries(rows.map(r => [r.patient_id, r.month])));
    } catch (err) {
      console.error('Failed to fetch current client list', err);
    }
  }, [monthParam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPatients(), fetchMembership()]).finally(() => setLoading(false));
  }, [fetchPatients, fetchMembership]);

  // Add / remove for the selected month ONWARDS. Earlier months keep what they
  // had; later months follow along until the team makes another change there.
  const setMembership = async (p: Patient, onList: boolean) => {
    const wasOnList = included.has(p.id);
    const prevChangedIn = changedIn[p.id];

    setIncluded(prev => {
      const n = new Set(prev);
      onList ? n.add(p.id) : n.delete(p.id);
      return n;
    });
    setChangedIn(prev => ({ ...prev, [p.id]: monthParam }));

    try {
      await axios.post(
        `${API_BASE_URL}/api/billing/current-clients/override`,
        { patient_id: p.id, month: monthParam, included: onList },
        { headers },
      );
      const who = clientNameOf(p) || p.patient_name;
      toast.success(
        onList
          ? `${who} added from ${monthLabel(selectedMonth)} onwards`
          : `${who} removed from ${monthLabel(selectedMonth)} onwards`,
      );
    } catch (err) {
      console.error('Failed to update current client list', err);
      setIncluded(prev => {          // revert
        const n = new Set(prev);
        wasOnList ? n.add(p.id) : n.delete(p.id);
        return n;
      });
      setChangedIn(prev => {
        const n = { ...prev };
        if (prevChangedIn === undefined) delete n[p.id]; else n[p.id] = prevChangedIn;
        return n;
      });
      toast.error('Could not update the list. Please try again.');
    }
  };

  const [pendingRemoval, setPendingRemoval] = useState<Patient | null>(null);
  const removalWho = pendingRemoval
    ? (clientNameOf(pendingRemoval) || pendingRemoval.patient_name)
    : '';

  const matchesSearch = (p: Patient, term: string) => {
    const q = term.toLowerCase();
    if (!q) return true;
    const anyClientMatch = (p.patient_clients || []).some(c =>
      (c.client_name || c.name || '').toLowerCase().includes(q));
    const td0 = p.treating_details?.[0];
    return (
      p.patient_name.toLowerCase().includes(q) ||
      anyClientMatch ||
      `LHHS${p.id}`.toLowerCase().includes(q) ||
      (td0?.doctor || '').toLowerCase().includes(q) ||
      (td0?.hospital || '').toLowerCase().includes(q) ||
      (p.address || '').toLowerCase().includes(q) ||
      (p.duty_type || '').toLowerCase().includes(q) ||
      (p.case_manager || '').toLowerCase().includes(q)
    );
  };

  const byCaseStart = (a: Patient, b: Patient) => {
    if (!a.case_start_date) return 1;
    if (!b.case_start_date) return -1;
    return a.case_start_date.localeCompare(b.case_start_date);
  };

  // Duty type in the client's reading order (08 Hrs, 12 Dual, 12 Day, 12 Night,
  // 24 Hrs), then A→Z on client name — the same ordering the Client List uses.
  const byDutyThenName = (a: Patient, b: Patient) => compareByDutyThenName(
    { duty_type: a.duty_type, name: clientNameOf(a) || a.patient_name },
    { duty_type: b.duty_type, name: clientNameOf(b) || b.patient_name },
  );

  const onList = patients.filter(p => included.has(p.id));
  const filtered = onList.filter(p => matchesSearch(p, searchTerm)).sort(byDutyThenName);

  // Everyone not on this month's list — which on an opt-in list is most of the
  // Client List, not just the cases someone took off.
  //
  // Newest case first, the opposite of the table: the reason to open this picker
  // is almost always a case that has just started, and the long tail of closed
  // cases from years back would otherwise bury it.
  const offList = patients
    .filter(p => !included.has(p.id))
    .filter(p => matchesSearch(p, addSearch))
    .sort((a, b) => byCaseStart(b, a));

  // Cases that started recently and still aren't on the list — the ones at risk
  // of going unbilled now that nothing lands here automatically.
  const NEW_CASE_WINDOW_DAYS = 60;
  const newCutoff = new Date(Date.now() - NEW_CASE_WINDOW_DAYS * 86400000)
    .toISOString().slice(0, 10);
  const isRecent = (p: Patient) => !!p.case_start_date && p.case_start_date >= newCutoff;
  const unlistedRecentCount = patients.filter(p => !included.has(p.id) && isRecent(p)).length;

  // ── Styles matching BillingIndex exactly ─────────────────────────────────
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' },
    title: { margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: 0, color: '#64748b', fontSize: '0.85rem' },
    searchInput: {
      padding: '0.5rem 0.75rem 0.5rem 2rem', borderRadius: '8px', border: '1px solid #cbd5e1',
      fontSize: '0.85rem', color: '#334155', width: '240px', outline: 'none', backgroundColor: '#fff',
    },
    tableCard: {
      backgroundColor: 'white', borderRadius: '12px',
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.025)',
      border: '1px solid #e2e8f0', overflow: 'hidden',
    },
    tableWrapper: { width: '100%', overflowX: 'auto' },
    table: { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', color: '#1d2631' },
    thead: { backgroundColor: '#86afd8', borderBottom: '1px solid #e2e8f0' },
    th: {
      padding: '0.55rem 0.3rem', color: '#475569', fontWeight: 700, fontSize: '0.68rem',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      border: '1px solid #c5cbd3', textAlign: 'center', lineHeight: 1.3,
    },
    tbody: { backgroundColor: 'white' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.1s' },
    td: { padding: '0.45rem 0.35rem', verticalAlign: 'middle', border: '1px solid #c5cbd3', textAlign: 'center', fontSize: '0.76rem', wordBreak: 'break-word', lineHeight: 1.35 },
    textBtn: {
      padding: '0.35rem 0.7rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
      cursor: 'pointer', border: 'none', transition: 'all 0.2s', display: 'inline-block',
    },
    // View and Edit stack inside their shared cell — side by side they'd force
    // the column wider than the pair is worth. Stretch, not centre + min-width:
    // at 4% the column can be narrower than a fixed button on a smaller screen,
    // and a stretched button takes the width it's given instead of spilling out.
    btnStack: { display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'stretch' },
    textBtnSm: { padding: '0.15rem 0.3rem', fontSize: '0.65rem' },
    monthBar: {
      backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
      padding: '0.85rem 1.25rem', marginBottom: '1.25rem',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
    },
    stepBtn: {
      padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
      background: 'white', cursor: 'pointer', fontSize: '0.88rem', color: '#475569',
    },
  };

  const btnView   = { background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.2)' };
  const btnEdit   = { background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)' };
  const btnBill   = { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(22,163,74,0.2)' };
  const btnCreate = { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(217,119,6,0.2)' };
  const btnRemove = { background: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(220,38,38,0.2)' };

  const isCurrentMonth = selectedMonth === currentYearMonth();

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Current Client List</h1>
          <p style={styles.subtitle}>The cases being billed this month. Nothing lands here on its own — add the cases that bill.</p>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <svg width="16" height="16" fill="none" stroke="#94a3b8" viewBox="0 0 24 24"
            style={{ position: 'absolute', left: '10px', pointerEvents: 'none' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search name, doctor, hospital..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </header>

      {/* Month navigation + Add Case */}
      <div style={styles.monthBar}>
        <button style={styles.stepBtn} onClick={() => setSelectedMonth(prevMonth(selectedMonth))}>
          ← Previous
        </button>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
            {monthLabel(selectedMonth)}
          </div>
          <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {isCurrentMonth && (
              <span style={{
                fontSize: '0.72rem', backgroundColor: '#dbeafe', color: '#1e40af',
                padding: '2px 8px', borderRadius: '9999px', fontWeight: 600,
              }}>
                Current Month
              </span>
            )}
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              {onList.length} of {patients.length} clients billing
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button
            onClick={() => { setAddingCase(true); setAddSearch(''); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '8px 14px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(37,99,235,0.25)', whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontSize: '15px', lineHeight: 1 }}>+</span> Add Case
            {/* Nothing reaches this list on its own any more, so a case that
                started recently and still isn't on it is the one way a client
                goes unbilled. The count makes that visible without opening
                the picker. */}
            {unlistedRecentCount > 0 && (
              <span
                title={`${unlistedRecentCount} case${unlistedRecentCount === 1 ? '' : 's'} started in the last ${NEW_CASE_WINDOW_DAYS} days and ${unlistedRecentCount === 1 ? 'is' : 'are'} not on this list`}
                style={{
                  background: '#fff', color: '#1d4ed8', borderRadius: '9999px',
                  fontSize: '0.7rem', fontWeight: 800, padding: '0 6px', lineHeight: '16px',
                }}
              >
                {unlistedRecentCount} new
              </span>
            )}
          </button>
          <button style={styles.stepBtn} onClick={() => setSelectedMonth(nextMonth(selectedMonth))}>
            Next →
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>Loading patient data...</div>
      ) : (
        <div style={styles.tableCard}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  {/* Percentages summing to 100, so the table fits its container
                      and never scrolls sideways. Room for Duty Type comes from
                      Address / Doctor / Hospital / Patient — the roomiest text
                      columns — and from the narrower View / Edit cell. */}
                  <th style={{ ...styles.th, width: '3%' }}>Sl.<br/>No</th>
                  <th style={{ ...styles.th, width: '5%' }}>Case<br/>Start</th>
                  <th style={{ ...styles.th, width: '5%' }}>Duty<br/>Type</th>
                  <th style={{ ...styles.th, width: '9%' }}>Client Name</th>
                  <th style={{ ...styles.th, width: '7%' }}>Mobile</th>
                  <th style={{ ...styles.th, width: '9%' }}>Patient Name</th>
                  <th style={{ ...styles.th, width: '3%' }}>Age /<br/>Sex</th>
                  <th style={{ ...styles.th, width: '10%' }}>Address</th>
                  <th style={{ ...styles.th, width: '11%' }}>Doctor<br/>Name</th>
                  <th style={{ ...styles.th, width: '8%' }}>Hospital<br/>Name</th>
                  <th style={{ ...styles.th, width: '8%' }}>Case<br/>Manager</th>
                  {/* View and Edit share a cell — two narrow columns of one
                      button each cost more width than the pair needs. */}
                  <th style={{ ...styles.th, width: '4%' }}>View / Edit<br/>Client</th>
                  <th style={{ ...styles.th, width: '6%' }}>View<br/>Bill</th>
                  <th style={{ ...styles.th, width: '6%' }}>Create<br/>Bill</th>
                  <th style={{ ...styles.th, width: '6%' }}>Remove<br/>Case</th>
                </tr>
              </thead>
              <tbody style={styles.tbody}>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={15} style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                      {searchTerm
                        ? 'No patients found.'
                        : `No cases on the list for ${monthLabel(selectedMonth)} yet. Use “Add Case” to put the billing cases on it.`}
                    </td>
                  </tr>
                ) : (
                  filtered.map((p, idx) => {
                    const clients = [...(p.patient_clients || [])].sort((a: any, b: any) => (a.id || 0) - (b.id || 0));
                    const td0 = p.treating_details?.[0];
                    const doctor = td0?.doctor || '—';
                    const hospital = td0?.hospital || '—';

                    return (
                      <tr
                        key={p.id}
                        style={{ ...styles.tr, backgroundColor: hoverRowId === p.id ? '#f8fafc' : 'white' }}
                        onMouseEnter={() => setHoverRowId(p.id)}
                        onMouseLeave={() => setHoverRowId(null)}
                      >
                        {/* Sl. No */}
                        <td style={{ ...styles.td, backgroundColor: '#f8fafc', fontWeight: 600, color: '#94a3b8' }}>
                          {idx + 1}
                        </td>

                        {/* Case Start Date */}
                        <td style={styles.td}>
                          {formatDate(p.case_start_date)}
                        </td>

                        {/* Duty Type — up front, since the list is ordered by it */}
                        <td style={styles.td}><DutyTypePill dutyType={p.duty_type} /></td>

                        {/* Client Name */}
                        <td style={{ ...styles.td, textAlign: 'left' }}>
                          {clients.length === 0 ? '—' : clients.map((c, i) => (
                            <div key={i} style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem', marginBottom: i < clients.length - 1 ? '2px' : 0 }}>
                              {c.client_name || c.name || '—'}
                            </div>
                          ))}
                        </td>

                        {/* Mobile */}
                        <td style={styles.td}>
                          {clients.length === 0 ? '—' : clients.map((c, i) => (
                            <div key={i} style={{ fontSize: '0.76rem', marginBottom: i < clients.length - 1 ? '2px' : 0 }}>
                              {c.contact_number_1 || c.contact_number || '—'}
                            </div>
                          ))}
                        </td>

                        {/* Patient Name + CID */}
                        <td style={{ ...styles.td, textAlign: 'left' }}>
                          <div style={{ color: '#0f172a', fontSize: '0.78rem' }}>{p.patient_name}</div>
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontFamily: 'monospace', marginTop: '1px' }}>
                            LHHS{p.id}
                          </div>
                        </td>

                        {/* Age / Sex */}
                        <td style={styles.td}>
                          {p.age || '—'}/{p.sex ? p.sex.charAt(0).toUpperCase() : '—'}
                        </td>

                        {/* Address */}
                        <td style={{ ...styles.td, textAlign: 'left', color: '#1e293b' }}>
                          {p.address || '—'}
                        </td>

                        {/* Doctor Name */}
                        <td style={styles.td}>{doctor}</td>

                        {/* Hospital Name */}
                        <td style={styles.td}>{hospital}</td>

                        {/* Case Manager */}
                        <td style={styles.td}>{p.case_manager || '—'}</td>

                        {/* View + Edit client */}
                        <td style={styles.td}>
                          <div style={styles.btnStack}>
                            <button style={{ ...styles.textBtn, ...styles.textBtnSm, ...btnView }} onClick={() => setViewPatientId(p.id)}>
                              View
                            </button>
                            <button style={{ ...styles.textBtn, ...styles.textBtnSm, ...btnEdit }} onClick={() => setEditPatientId(p.id)}>
                              Edit
                            </button>
                          </div>
                        </td>

                        {/* View Bill */}
                        <td style={styles.td}>
                          <button style={{ ...styles.textBtn, ...btnBill }} onClick={() => navigate(`/billing/patient/${p.id}`, { state: { from: '/billing/current-clients' } })}>
                            View Bill
                          </button>
                        </td>

                        {/* Create Bill */}
                        <td style={styles.td}>
                          <button style={{ ...styles.textBtn, ...btnCreate }} onClick={() => setPickerPatient(p)}>
                            Create Bill
                          </button>
                        </td>

                        {/* Remove from this month onwards */}
                        <td style={styles.td}>
                          <button style={{ ...styles.textBtn, ...btnRemove }} onClick={() => setPendingRemoval(p)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Remove confirmation. Says plainly what leaves, what stays, and that
          it's reversible — the three things the old window.confirm buried. */}
      <ConfirmDialog
        open={pendingRemoval !== null}
        danger
        title={`Remove ${removalWho} from this list?`}
        message="They will stop showing on the Current Client List."
        points={[
          `Takes effect from ${monthLabel(selectedMonth)}, and stays that way for the months after it.`,
          `Months before ${monthLabel(selectedMonth)} are not changed.`,
          'Nothing is deleted — the client stays on the main Client List.',
          'You can add them back at any time with the Add Case button.',
        ]}
        confirmLabel="Remove from list"
        onConfirm={() => {
          if (pendingRemoval) setMembership(pendingRemoval, false);
          setPendingRemoval(null);
        }}
        onCancel={() => setPendingRemoval(null)}
      />

      {/* Add case modal — the cases taken off this month's list */}
      {addingCase && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 5000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)',
          }}
          onClick={() => { setAddingCase(false); setAddSearch(''); }}
        >
          <div
            style={{
              background: '#fff', borderRadius: '14px', width: '460px', maxWidth: '92vw', maxHeight: '80vh',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Add Case to {monthLabel(selectedMonth)}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  Cases not on the list, newest first. Adding applies from this month onwards.
                </div>
              </div>
              <button
                onClick={() => { setAddingCase(false); setAddSearch(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px', lineHeight: 1, width: '28px', height: '28px' }}
              >
                ✕
              </button>
            </div>
            <input
              autoFocus
              value={addSearch}
              onChange={e => setAddSearch(e.target.value)}
              placeholder="Search patient name or client…"
              style={{ width: '100%', padding: '11px 16px', border: 'none', borderBottom: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', background: '#fafafa', fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box' }}
            />
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '340px' }}>
              {offList.length === 0 ? (
                <div style={{ padding: '28px 24px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                  {addSearch.trim()
                    ? 'No matching cases are off the list.'
                    : 'Every client is already on this month’s list.'}
                </div>
              ) : offList.map(p => {
                const client = clientNameOf(p);
                const since = changedIn[p.id];
                return (
                  <div
                    key={p.id}
                    onClick={() => { setMembership(p, true); setAddingCase(false); setAddSearch(''); }}
                    style={{ padding: '11px 16px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>
                      {(client || p.patient_name)?.[0] || '?'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {client || p.patient_name}
                      </div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.patient_name} · LHHS{p.id}
                        {p.case_start_date ? ` · started ${formatDate(p.case_start_date)}` : ''}
                        {/* Only set when the case was explicitly taken off; a
                            case that was never added has no row to date. */}
                        {since ? ` · off since ${monthLabel(since.slice(0, 7))}` : ''}
                      </div>
                    </div>
                    {isRecent(p) && (
                      <span style={{
                        fontSize: '9px', fontWeight: 800, color: '#b45309', background: '#fef3c7',
                        borderRadius: '9999px', padding: '2px 6px', flexShrink: 0, whiteSpace: 'nowrap',
                      }}>NEW</span>
                    )}
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb', flexShrink: 0 }}>+ Add</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bill type picker modal */}
      {pickerPatient && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)',
            zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPickerPatient(null)}
        >
          <div
            style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '2rem',
              width: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
              Select Bill Type
            </h3>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#64748b' }}>
              Choose the type of invoice to create for {pickerPatient.patient_name}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {BILL_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    const p = pickerPatient;
                    setPickerPatient(null);
                    if (t.key === 'consumables') {
                      navigate(`/billing/consumables/new/${p.id}`);
                    } else if (t.key === 'consumable_return') {
                      navigate(`/billing/consumables/return/new/${p.id}`);
                    } else if (t.key === 'equipment_rental') {
                      navigate(`/billing/equipment-rental/new/${p.id}`, { state: { billType: 'equipment_rental' } });
                    } else if (t.key === 'equipment_sale') {
                      navigate(`/billing/equipment-sale/new/${p.id}`, { state: { billType: 'equipment_sale' } });
                    } else {
                      navigate(`/billing/new/${p.id}`, { state: { billType: t.key } });
                    }
                  }}
                  style={{
                    padding: '1rem', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: t.bg, border: `1.5px solid ${t.border}`,
                    borderRadius: '10px', transition: 'transform 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: t.color }}>{t.label}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{t.desc}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setPickerPatient(null)}
              style={{
                marginTop: '1.25rem', width: '100%', padding: '0.6rem',
                background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px',
                cursor: 'pointer', color: '#64748b', fontSize: '0.85rem',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* View patient modal */}
      <ViewPatientModal
        isOpen={viewPatientId !== null}
        patientId={viewPatientId}
        onClose={() => setViewPatientId(null)}
      />

      {/* Edit patient modal */}
      <EditPatientModal
        isOpen={editPatientId !== null}
        patientId={editPatientId}
        onClose={() => setEditPatientId(null)}
        onSuccess={() => { fetchPatients(); setEditPatientId(null); }}
      />
    </div>
  );
};

export default CurrentClientList;
